/**
 * Chat presence: which chat, if any, this device is showing to the guest right now.
 *
 * Two consumers:
 * - Push filtering on this device (`isViewingChat`): a new-chat-message push for the chat the guest is
 *   already looking at is dropped instead of shown.
 * - The server (`ReportChatPresence`): while the guest views a chat with a reply generating, this
 *   device reports it, so the reply's push is skipped on every other device too (no phone buzz while
 *   chatting on web). Reports go out on each change and as a heartbeat; the server ignores reports
 *   older than its TTL, so a device that vanishes stops suppressing pushes on its own.
 */
import type { ReportChatPresenceFlowSpec } from '@elara/domain/flows';

import type { AttentionPort } from '#lib/attention-port.js';
import type { Route } from './routing-contract.js';

import { effect } from '@preact/signals-react';

import { CHAT_PRESENCE_HEARTBEAT_MS } from '@elara/domain/flows';

import { createState } from '#lib/signals-utils.js';

import { isTripRoutePath } from './routing-contract.js';

type ReportChatPresenceInput = ReportChatPresenceFlowSpec['InputType'];

/**
 * The chat the guest is looking at, or `undefined`: attentive, on a route for the chat's own trip, and
 * with the chat itself on screen (which differs by platform, so the caller decides).
 *
 * @example
 * resolveViewingChatId({
 *   route: { path: '/trip/t1/chat', params: { id: 't1' }, query: {} },
 *   tripId: 't1', chatId: 'c1', attentive: true, chatOnScreen: true,
 * }); // 'c1'
 */
export function resolveViewingChatId({
  route,
  tripId,
  chatId,
  attentive,
  chatOnScreen,
}: {
  route: Route | undefined;
  tripId: string | undefined;
  chatId: string | undefined;
  attentive: boolean;
  chatOnScreen: boolean;
}): string | undefined {
  if (!attentive || !chatOnScreen || !route || !chatId || !tripId) return;
  if (!isTripRoutePath(route.path) || route.params.id !== tripId) return;
  return chatId;
}

export interface ChatPresenceDeps {
  routerState: { readonly currentRoute: Route | undefined };
  tripsState: { readonly current: { id: string } | undefined };
  chatsState: { readonly current: { id: string } | undefined; readonly isStreaming: boolean };
  sessionState: { readonly userId: string | undefined };
  attention: AttentionPort;
  /**
   * Whether the trip's chat is on screen for this trip route. Web: the chat panel is visible (always
   * on desktop, the active panel on narrow screens). Mobile: the route is the chat tab.
   */
  isChatOnScreen: (route: Route) => boolean;
  /** Sends one presence report (the `ReportChatPresence` flow). */
  report: (input: ReportChatPresenceInput, options?: { keepalive?: boolean }) => Promise<unknown>;
  /** This device's stable install id (Firebase Installation ID, or a persisted fallback). */
  getInstallId: () => Promise<string>;
}

export function createChatPresenceState(deps: ChatPresenceDeps) {
  const { routerState, tripsState, chatsState, sessionState, attention } = deps;

  /** The chat this device last told the server it is viewing; plain closure state, not a signal. */
  let reportedChatId: string | undefined;
  let heartbeat: ReturnType<typeof setInterval> | undefined;
  let installId: Promise<string> | undefined;

  const send = (chatId: string, viewing: boolean, keepalive = false): void => {
    installId ??= deps.getInstallId();
    void installId
      .then(async (id) => deps.report({ chatId, viewing, installId: id }, keepalive ? { keepalive } : undefined))
      .catch((error: unknown) => {
        // Best-effort: a lost report only means one extra or one missing push.
        console.debug('[ChatPresenceState] Presence report failed', error);
      });
  };

  const stopHeartbeat = (): void => {
    if (heartbeat) clearInterval(heartbeat);
    heartbeat = undefined;
  };

  const startHeartbeat = (chatId: string): void => {
    send(chatId, true);
    heartbeat = setInterval(() => send(chatId, true), CHAT_PRESENCE_HEARTBEAT_MS);
  };

  /** Moves the server-side report to `next` (a chat id, or nothing). */
  const transition = (next: string | undefined, keepalive = false): void => {
    if (next === reportedChatId) {
      // Same chat again after a pause (a new reply started): resume the heartbeat.
      if (next && !heartbeat) startHeartbeat(next);
      return;
    }
    stopHeartbeat();
    if (reportedChatId) send(reportedChatId, false, keepalive);
    reportedChatId = next;
    if (next) startHeartbeat(next);
  };

  const state = createState({
    /** Mirrors {@link AttentionPort.isAttentive} as a signal. */
    attentive: attention.isAttentive(),

    /** Cleanup for the running monitors; presence means "running". */
    stopMonitors: undefined as (() => void) | undefined,

    /** The chat the guest is looking at on this device, or `undefined`. */
    get viewingChatId(): string | undefined {
      const route = routerState.currentRoute;
      return resolveViewingChatId({
        route,
        tripId: tripsState.current?.id,
        chatId: chatsState.current?.id,
        attentive: this.attentive,
        chatOnScreen: !!route && deps.isChatOnScreen(route),
      });
    },

    /** Whether this device is showing `chatId` to the guest right now (push filtering). */
    isViewingChat(chatId: string): boolean {
      return this.viewingChatId === chatId;
    },

    /**
     * Mirrors attention and reports presence while a reply is generating — the window in which a push
     * can fire. Idempotent: repeated calls return the existing cleanup function.
     */
    startMonitors(): () => void {
      if (this.stopMonitors) return this.stopMonitors;

      const unsubscribeAttention = attention.onChange((attentive, { unloading }) => {
        // The page is going away: report "not viewing" now, with keepalive, before the effect below
        // would send it with a request the browser cancels.
        if (unloading) transition(undefined, true);
        state.attentive = attentive;
      });

      const disposeEffect = effect(() => {
        const viewing = state.viewingChatId;
        const generating = !!sessionState.userId && chatsState.isStreaming;
        if (viewing && generating) {
          transition(viewing);
        } else if (viewing && viewing === reportedChatId) {
          // The reply just finished with the guest still watching. Keep the server's "viewing" record
          // (it lapses by TTL) rather than racing the server's own presence check with a "not viewing".
          stopHeartbeat();
        } else {
          transition(undefined);
        }
      });

      this.stopMonitors = () => {
        unsubscribeAttention();
        disposeEffect();
        transition(undefined);
        state.stopMonitors = undefined;
      };
      return this.stopMonitors;
    },
  });

  return state;
}

export type ChatPresenceState = ReturnType<typeof createChatPresenceState>;
